<template>
  <div
    v-if="properties && properties.length > 0"
    data-component="property-paginator"
    class="tw-pointer-events-none"
  >
    <!-- Swipeable Card Container -->
    <div
      class="tw-fixed tw-left-[16px] tw-right-[16px] tw-z-50 tw-transform tw-transition-all tw-duration-300 tw-ease-in-out"
      :class="[
        properties && properties.length > 0
          ? 'tw-translate-y-0'
          : 'tw-translate-y-full',
        properties.length > 1 ? 'tw-bottom-16' : 'tw-bottom-4',
      ]"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- Card Wrapper with Transform -->
      <div
        ref="cardContainer"
        class="tw-pointer-events-auto tw-mx-auto tw-flex tw-max-w-[500px] tw-gap-[18px] tw-transition-transform tw-duration-300 tw-ease-out"
        :style="{ transform: `translateX(${cardOffset}px)` }"
      >
        <PropertyCard
          v-for="(property, index) in properties"
          :key="property.id"
          :property="property"
          :selected-university="selectedUniversity"
          :route-data="routeData"
          :enable-housemate-groups="enableHousemateGroups"
          :enable-shortlist-in-search="enableShortlistInSearch"
          :show-housemate-group-tab="showHousemateGroupTab"
          :is-logged-in-student="isLoggedInStudent"
          @close="closePaginator"
          @shortlist-changed="handleShortlistChanged"
        />
      </div>
    </div>

    <!-- Pagination Controls (only show if multiple properties) - Outside white background -->
    <div
      v-if="properties.length > 1"
      class="tw-pointer-events-auto tw-fixed tw-bottom-4 tw-left-1/2 tw-z-50 tw-flex tw--translate-x-1/2 tw-transform tw-items-center tw-justify-center tw-gap-4 tw-rounded-3xl tw-bg-gray-100/90"
    >
      <!-- Previous Button -->
      <div
        :disabled="currentIndex === 0"
        class=":disabled:tw-bg-white tw-flex tw-h-10 tw-w-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-300 tw-bg-white tw-transition-colors hover:tw-bg-gray-50 disabled:tw-cursor-not-allowed"
        @click="previousProperty"
      >
        <img
          :src="
            currentIndex === 0
              ? cdnAsset('svg/phosphor/caret-left-disabled.svg')
              : cdnAsset('svg/phosphor/caret-left.svg')
          "
          class="tw-h-4 tw-w-4"
          alt="Cycling"
        />
      </div>

      <!-- Pagination Dots -->
      <div
        class="tw-flex tw-max-w-[220px] tw-items-center tw-justify-center tw-gap-2 tw-overflow-hidden"
      >
        <!-- Show first dot (always visible when more than 7 properties) -->
        <button
          v-if="properties.length > 7"
          type="button"
          class="tw-h-3 tw-w-3 tw-rounded-full tw-border-2 tw-transition-colors"
          :class="
            currentIndex === 0
              ? 'tw-border-uhblue-600 tw-bg-uhblue-600'
              : 'tw-border-uhblue-600 tw-bg-white'
          "
          @click="goToProperty(0)"
        ></button>

        <!-- Show ellipsis if we're far from start -->
        <button
          v-if="properties.length > 7 && currentIndex > 3"
          type="button"
          class="tw-h-2 tw-w-2 tw-rounded-full tw-border-2 tw-border-uhblue-600 tw-bg-white tw-transition-colors"
        ></button>

        <!-- Show visible range of dots -->
        <button
          v-for="index in visibleDotRange"
          :key="index"
          type="button"
          class="tw-h-3 tw-w-3 tw-rounded-full tw-border-2 tw-transition-colors"
          :class="
            index === currentIndex
              ? 'tw-border-uhblue-600 tw-bg-uhblue-600'
              : 'tw-border-uhblue-600 tw-bg-white'
          "
          @click="goToProperty(index)"
        ></button>

        <!-- Show ellipsis if we're far from end -->
        <button
          v-if="properties.length > 7 && currentIndex < properties.length - 4"
          type="button"
          class="tw-h-2 tw-w-2 tw-rounded-full tw-border-2 tw-border-uhblue-600 tw-bg-white tw-transition-colors"
        ></button>

        <!-- Show last dot (always visible when more than 7 properties) -->
        <button
          v-if="properties.length > 7"
          type="button"
          class="tw-h-3 tw-w-3 tw-rounded-full tw-border-2 tw-transition-colors"
          :class="
            currentIndex === properties.length - 1
              ? 'tw-border-uhblue-600 tw-bg-uhblue-600'
              : 'tw-border-uhblue-600 tw-bg-white'
          "
          @click="goToProperty(properties.length - 1)"
        ></button>
      </div>

      <!-- Next Button -->
      <div
        :disabled="currentIndex === properties.length - 1"
        class=":disabled:tw-bg-white tw-flex tw-h-10 tw-w-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-300 tw-bg-white tw-transition-colors hover:tw-bg-gray-50 disabled:tw-cursor-not-allowed"
        @click="nextProperty"
      >
        <img
          :src="
            currentIndex === properties.length - 1
              ? cdnAsset('svg/phosphor/caret-right-disabled.svg')
              : cdnAsset('svg/phosphor/caret-right.svg')
          "
          class="tw-h-4 tw-w-4"
          alt="Cycling"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PropertyCard from './PropertyCard.vue';

export default {
  name: 'PropertyPaginator',
  components: {
    PropertyCard,
  },
  props: {
    enableHousemateGroups: {
      type: Boolean,
      default: false,
    },
    enableShortlistInSearch: {
      type: Boolean,
      default: false,
    },
    showHousemateGroupTab: {
      type: Boolean,
      default: true,
    },
    isLoggedInStudent: {
      type: Boolean,
      default: false,
    },
    properties: {
      type: Array,
      default: () => [],
    },
    routeData: {
      type: Object,
      default: () => null,
    },
    selectedUniversity: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      currentIndex: 0,
      // Touch/Swipe properties
      touchStartX: 0,
      touchStartY: 0,
      touchCurrentX: 0,
      isDragging: false,
      cardOffset: 0,
      containerWidth: 0,
    };
  },
  computed: {
    currentProperty() {
      return this.properties[this.currentIndex] || null;
    },
    visibleDotRange() {
      const total = this.properties.length;
      const current = this.currentIndex;

      // If 7 or fewer properties, show all dots
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i);
      }

      const showStartEllipsis = current > 3;
      const showEndEllipsis = current < total - 4;

      let start, end;

      if (showStartEllipsis && showEndEllipsis) {
        // Both ellipsis visible: show 6 dots in middle
        start = Math.max(1, current - 2);
        end = Math.min(total - 2, current + 3);

        // Ensure exactly 6 dots
        if (end - start + 1 < 6) {
          if (start === 1) {
            end = Math.min(total - 2, start + 5);
          } else {
            start = Math.max(1, end - 5);
          }
        }
      } else {
        // One ellipsis visible: show 7 dots
        start = Math.max(1, current - 3);
        end = Math.min(total - 2, current + 3);

        // Ensure exactly 7 dots
        if (end - start + 1 < 7) {
          if (start === 1) {
            end = Math.min(total - 2, start + 6);
          } else {
            start = Math.max(1, end - 6);
          }
        }
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  watch: {
    properties: {
      handler(newProperties) {
        // Reset to first property when properties change
        this.currentIndex = 0;
        this.$nextTick(() => {
          this.updateContainerWidth();
        });
      },
      immediate: true,
    },
  },
  mounted() {
    this.updateContainerWidth();
    window.addEventListener('resize', this.updateContainerWidth);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateContainerWidth);
  },
  methods: {
    closePaginator() {
      this.$emit('close');
    },
    handleShortlistChanged(property) {
      this.$emit('shortlist-changed', property);
    },
    previousProperty() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.updateCardOffset();
      }
    },
    nextProperty() {
      if (this.currentIndex < this.properties.length - 1) {
        this.currentIndex++;
        this.updateCardOffset();
      }
    },
    goToProperty(index) {
      if (index >= 0 && index < this.properties.length) {
        this.currentIndex = index;
        this.updateCardOffset();
      }
    },
    // Touch/Swipe Methods
    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isDragging = true;
      const gapSize = 18;
      this.cardOffset = -this.currentIndex * (this.containerWidth + gapSize);
    },
    handleTouchMove(event) {
      if (!this.isDragging) return;

      event.preventDefault();
      this.touchCurrentX = event.touches[0].clientX;
      const deltaX = this.touchCurrentX - this.touchStartX;
      const gapSize = 18;
      this.cardOffset =
        -this.currentIndex * (this.containerWidth + gapSize) + deltaX;
    },
    handleTouchEnd(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      const deltaX = this.touchCurrentX - this.touchStartX;
      const threshold = this.containerWidth * 0.3; // 30% of card width

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0 && this.currentIndex > 0) {
          this.previousProperty();
        } else if (
          deltaX < 0 &&
          this.currentIndex < this.properties.length - 1
        ) {
          this.nextProperty();
        } else {
          this.updateCardOffset();
        }
      } else {
        this.updateCardOffset();
      }
    },
    updateCardOffset() {
      const gapSize = 18; // 4 * 4px = 16px gap
      this.cardOffset = -this.currentIndex * (this.containerWidth + gapSize);
    },
    updateContainerWidth() {
      this.$nextTick(() => {
        if (this.$refs.cardContainer) {
          this.containerWidth = this.$refs.cardContainer.offsetWidth;
          this.updateCardOffset();
        } else {
          // Fallback: use the main container element minus padding
          this.containerWidth = this.$el.offsetWidth - 32; // Subtract left/right padding (16px each)
          this.updateCardOffset();
        }
      });
    },
    debouncedResize() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.updateContainerWidth();
      }, 150);
    },
  },
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
